import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { OptionSelector } from "@/components/reusables/OptionSelectors/OptionSelector";
import OptionsSheet from "@/components/reusables/OptionSelectors/OptionSheet";
import SingleFileUpload from "@/components/reusables/SingleFileUpload";
import FormWrapper from "@/components/reusables/custom-form-fields/FormWrapper";
import { FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ClaimsOptions, OptionTypeConstants } from "@/constants/constants";
import { PR_Message } from "@/constants/project-register.constant";
import {
  useGetClaimsOptionsQuery,
  useMutateClaimOptionMutation,
} from "@/redux/api/claimsQuery";
import { PoDetailsSchema, TAddProjectSchema } from "@/schemas/project.schema";

type ExtendedTProject =
  TAddProjectSchema extends z.infer<typeof PoDetailsSchema>
    ? TAddProjectSchema
    : TAddProjectSchema & z.infer<typeof PoDetailsSchema>;

const PoDetails = (): JSX.Element => {
  const [option, setOption] = useState({
    isOpen: false,
    statusFor: OptionTypeConstants.PROJECT_REGISTER_STATUS as string,
  });

  const [alterClaimsOption] = useMutateClaimOptionMutation();

  const form = useFormContext<ExtendedTProject>();
  const { data: poStatusList } = useGetClaimsOptionsQuery({
    forType: ClaimsOptions.PO_STATUS,
  });

  return (
    <>
      <h2 className='col-span-3 text-lg font-medium'>PO Details</h2>
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4 text-xs border border-input p-[26px] px-[18px] pb-[20px] rounded-md'>
        <FormField
          name='poNumber'
          control={form.control}
          render={({ field }) => (
            <FormWrapper label='PO Number'>
              <Input {...field} placeholder='Enter PO Number' />
            </FormWrapper>
          )}
        />
        <FormField
          name='poStatus'
          control={form.control}
          render={({ field }) => (
            <FormWrapper label='PO Status'>
              <OptionSelector
                activeId={field.value || ""}
                optionList={poStatusList || []}
                setIsOption={setOption}
                onValueChange={(value) => {
                  field.onChange(value);

                  return Promise.resolve();
                }}
                statusFor={OptionTypeConstants.PO_STATUS}
                key={field.value}
                className='!w-full flex justify-between text-gray-300'
                placeholder='Select PO Status'
                showIcon
              />
            </FormWrapper>
          )}
        />
        <FormField
          name='poFile'
          control={form.control}
          render={({ field }) => (
            <FormWrapper label='PO File'>
              <SingleFileUpload
                customLabel='Upload Logo'
                {...field}
                files={form.watch("poFile") || []}
                setFiles={(files) =>
                  form.setValue("poFile", files, { shouldDirty: true })
                }
                type='imageBox'
                isFile={true}
              />
            </FormWrapper>
          )}
        />
      </div>
      <OptionsSheet
        isOpen={{
          isOpen: option.isOpen,
          statusFor: option.statusFor,
        }}
        toggleOpen={() => {
          setOption({
            isOpen: !option.isOpen,
            statusFor: option.statusFor,
          });
        }}
        optionFor={option.statusFor}
        optionList={poStatusList || []}
        actions={{
          addMutation: async (value) => {
            const res = await alterClaimsOption({
              data: value,
              method: "POST",
            });
            if (res?.data && "data" in res && "message" in res?.data) {
              toast.success(PR_Message.PR_STATUS_ADDED);
            }
          },
          deleteMutation: async (id) => {
            const res = await alterClaimsOption({
              optionId: id,
              method: "DELETE",
            });
            if (res?.data && "data" in res && "message" in res?.data) {
              toast.success(PR_Message.PR_STATUS_DELETED);
            }
          },
          editMutation: async (value) => {
            const res = await alterClaimsOption({
              data: value?.data,
              method: "PUT",
            });
            if (res?.data && "data" in res && "message" in res?.data) {
              toast.success(PR_Message.PR_STATUS_UPDATED);
            }
          },
        }}
      />
    </>
  );
};

export default PoDetails;
